import { Field } from "formik";
import React, { ReactChild } from "react";
import styled, { css } from "styled-components";
import { LoadingState } from "./states/LoadingState";
import { style } from "./style";
import { Container, DescriptionText, Icon, SvgIcon, LabelText, LoadingWrapper } from "./utils/CheckAndRadioUtils";
import { deprecationWarning } from "./utils/warning";

type RadioInputProps = {
  loading: boolean;
  hasIcon: boolean;
};

const warn = () => {
  deprecationWarning('"RadioGroup"', '"RadioV2"', "?path=/story/components-radio-v2");
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RadioInput = styled(({ hasIcon, loading, ...rest }) => <Field {...rest} />)<RadioInputProps>`
  ${(props) => (props.loading ? "display: none;" : "")};
  appearance: none;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
  border-radius: 50%;
  width: ${style.vars.fontSize.base};
  height: ${style.vars.fontSize.base};
  grid-row: 1 / ${(props) => (props.hasIcon ? "-1" : "1")};
  align-self: center;
  background-color: ${style.colors.white};
  box-sizing: border-box;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    background-color: ${style.colors.shadesOfGrey["1"]};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  &:checked {
    border: 4px solid ${style.colors.blue.dark};
  }
`;

export type RadioButtonProps = {
  value: string;
  name: string;
  label: React.ReactNode;
  /**
   * @deprecated prefer `checked: boolean`
   */
  selected?: string;
  description?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  icon?: string;
  iconSrc?: string;
  iconAltText?: string;
  checked?: boolean;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

/**
 * @deprecated Use `RadioV2` instead
 */
export const RadioButton: React.FC<RadioButtonProps> = ({
  value,
  name,
  label,
  description,
  selected,
  required = false,
  disabled = false,
  loading,
  className,
  icon,
  iconSrc,
  iconAltText = "",
  checked,
  ...inputProps
}) => {
  warn();

  checked = checked ?? selected === value;

  const id = `${name}-${value}`;
  const labelId = `${id}-label`;
  const hasIcon = !!iconSrc || !!icon;
  const isLoading = loading && checked && !disabled;
  const isBoxMode = !!description || hasIcon;

  return (
    <Container
      htmlFor={id}
      className={className}
      checked={checked}
      disabled={disabled}
      isBoxMode={isBoxMode}
      hasIcon={hasIcon}
      hasDescription={!!description}
    >
      <RadioInput
        id={id}
        aria-labelledby={labelId}
        role="radio"
        type="radio"
        checked={checked}
        aria-checked={checked}
        name={name}
        required={required}
        value={value}
        disabled={disabled}
        loading={isLoading}
        hasIcon={hasIcon}
        {...inputProps}
      />
      {isLoading && (
        <LoadingWrapper hasIcon={hasIcon}>
          <LoadingState />
        </LoadingWrapper>
      )}
      {hasIcon && (iconSrc ? <Icon src={iconSrc} alt={iconAltText} /> : icon && <SvgIcon icon={icon} />)}
      <LabelText id={labelId} checked={checked} hasDescription={!!description} isBoxMode={isBoxMode}>
        {label}
      </LabelText>
      {!!description && <DescriptionText $hasIcon={hasIcon}>{description}</DescriptionText>}
    </Container>
  );
};

type RadioGroupProps = {
  children: ReactChild[];
  vertical?: boolean;
  className?: string;
};

const RadioGroupDescriptionContainer = styled.div`
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.bold};
`;

type RadioGroupDescriptionProps = Parameters<typeof RadioGroupDescriptionContainer>[0];

/**
 * @deprecated Use `RadioV2` instead
 */
export const RadioGroupDescription: React.FC<RadioGroupDescriptionProps> = (props) => {
  warn();

  return <RadioGroupDescriptionContainer {...props} />;
};

type RadioGroupContainerProps = {
  vertical?: boolean;
};

const RadioGroupContainer = styled.div<RadioGroupContainerProps>`
  align-items: flex-start;
  display: flex;
  ${(props) =>
    props.vertical
      ? css`
          flex-direction: column;
          label {
            margin-bottom: ${style.vars.whitespace.xsmall};
          }
        `
      : css`
          flex-direction: row;
          label {
            margin-right: ${style.vars.whitespace.medium};
          }
        `};
`;

/**
 * @deprecated Use `RadioV2` instead
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({ children, vertical, className }) => {
  warn();

  return (
    <RadioGroupContainer role="radiogroup" vertical={vertical} className={className}>
      {children}
    </RadioGroupContainer>
  );
};
